import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Trophy } from "lucide-react";

export interface QuizQuestion {
  id: string;
  question: string;
  answer_a: string;
  answer_b: string;
  answer_c: string;
  correct_answer: string;
  explanation: string | null;
}

interface ModuleQuizFinalProps {
  questions: QuizQuestion[];
  moduleTitle: string;
  onComplete: (answers: Record<string, string>) => void;
}

export default function ModuleQuizFinal({ questions, moduleTitle, onComplete }: ModuleQuizFinalProps) {
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const handleSubmit = () => {
    onComplete(answers);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-6 w-6 text-yellow-600" />
            Quiz Final - {moduleTitle}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <p className="text-muted-foreground">
              Answer all {questions.length} questions to complete the module. You need 70% to pass.
            </p>

            {questions.map((question, idx) => (
              <Card key={question.id} className="border-l-4 border-l-primary">
                <CardHeader>
                  <CardTitle className="text-lg">
                    Question {idx + 1}: {question.question}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={answers[question.id] || ""}
                    onValueChange={(value) => setAnswers({ ...answers, [question.id]: value })}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="A" id={`${question.id}-a`} />
                      <Label htmlFor={`${question.id}-a`} className="cursor-pointer">
                        A. {question.answer_a}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="B" id={`${question.id}-b`} />
                      <Label htmlFor={`${question.id}-b`} className="cursor-pointer">
                        B. {question.answer_b}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="C" id={`${question.id}-c`} />
                      <Label htmlFor={`${question.id}-c`} className="cursor-pointer">
                        C. {question.answer_c}
                      </Label>
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>
            ))}

            <Button
              onClick={handleSubmit}
              disabled={Object.keys(answers).length !== questions.length}
              className="w-full"
              size="lg"
            >
              Submit Quiz
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

